'use client';
import { DeviceVariantType } from '@unifyapps/hooks/useScreen/types';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
// biome-ignore lint/style/noRestrictedImports: useKey from react-use handles hotkeys
import { useKey } from 'react-use';
import {
  createWalkthroughManagerStore,
  type UseCreateWalkthroughManagerStore,
} from './createWalkthroughManagerStore';
import type { CreateWalkthroughManagerStoreParams } from './types';

const WalkthroughManagerStoreContext = createContext<UseCreateWalkthroughManagerStore | null>(null);

export const useWalkthroughManagerStore = () => {
  const _useStore = useContext(WalkthroughManagerStoreContext);
  if (!_useStore) {
    throw new Error('Missing WalkthroughManagerStoreProvider');
  }
  return _useStore;
};

// Optional is used in InterfacePageResourceLoader because we may not always want walkthrough feature in all no-code apps, that is WalkthroughManagerStoreProvider might not be wrapped everywhere on InterfaceAppProviders
// For example, login pages, etc.
export const useWalkthroughManagerStoreOptional = () => {
  return useContext(WalkthroughManagerStoreContext);
};

type WalkthroughManagerStoreProviderProps = Omit<CreateWalkthroughManagerStoreParams, 'device'>;

export function WalkthroughManagerStoreProvider({
  children,
  instanceId,
  userId,
  mode,
}: React.PropsWithChildren<WalkthroughManagerStoreProviderProps>) {
  const [store] = useState(() =>
    createWalkthroughManagerStore({
      instanceId,
      device: DeviceVariantType.DESKTOP,
      userId,
      mode,
    }),
  );

  useEffect(() => {
    store.setState((prevState) => ({
      ...prevState,
      userId,
    }));
  }, [userId, store]);

  useKey('W', () => {
    console.log('Walkthrough store', store.getState());
  });

  return (
    <WalkthroughManagerStoreContext.Provider value={store}>
      {children}
    </WalkthroughManagerStoreContext.Provider>
  );
}
